import type { SxProps, Theme } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CONTROL_HEIGHT } from "../../../theme/tokens";
import { type DropdownItem, SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import { BORDERLESS_SELECT_SX } from "../../UIToolkit/Tables/ConfigTable/configTableSx";
import { HolidayGroupChip } from "./HolidayGroupChip";
import type { HolidayGroup } from "./holidayApi";
import { HOLIDAY_NONE, holidayGroupColor, holidayGroupLabel } from "./holidayFormat";

/**
 * The chip is taller than a line of text, so MUI's own padding would make the field 45px. Centre the chip in a
 * standard-height field instead, so it lines up with the fields beside it.
 */
const FIELD_SELECT_SX = {
	"&& .MuiSelect-select": { py: 0, minHeight: 0, height: CONTROL_HEIGHT.STANDARD, boxSizing: "border-box", display: "flex", alignItems: "center" },
} as const;

type Props = {
	groups: readonly HolidayGroup[];
	/** Null = no group. */
	value: string | null;
	/** Called only with a different value. Null = Not assigned. */
	onChange: (groupId: string | null) => void;
	/** Offer "Not assigned" (a person) — a holiday has no such choice once it has a group. */
	allowNone: boolean;
	/** The chip shown while there is no group, when "Not assigned" is not a choice ("Choose group"). */
	emptyLabel?: string;
	/** `table` blends into a table cell; `field` is a standard labelled form field. */
	variant: "table" | "field";
	/** The field's name: its floating label with `showLabel`, otherwise only used in messages. */
	label: string;
	/** Show `label` as the field's floating label. A table cell, or a field under its own heading, doesn't. */
	showLabel: boolean;
	disabled?: boolean;
	sx?: SxProps<Theme>;
	dataTestId: string;
};

/** Picks a holiday group: the chosen group shows as its coloured chip. */
export const HolidayGroupSelect: FC<Props> = ({ groups, value, onChange, allowNone, emptyLabel, variant, label, showLabel, disabled, sx, dataTestId }) => {
	const { t } = useTranslation();
	const items = useMemo<DropdownItem<string>[]>(
		() => [
			...groups.map((group) => ({ value: group.id, label: holidayGroupLabel(t, group), color: holidayGroupColor(groups, group.id).dot })),
			...(allowNone ? [{ value: HOLIDAY_NONE, label: t("holidays.group.notAssigned") }] : []),
		],
		[groups, allowNone, t],
	);
	const selected = value ?? (allowNone ? HOLIDAY_NONE : undefined);
	const renderValue = (current: string | string[]) => {
		const id = Array.isArray(current) ? current[0] : current;
		return <HolidayGroupChip groups={groups} groupId={id === HOLIDAY_NONE || id === "" ? null : id} emptyLabel={emptyLabel} />;
	};
	return (
		<SystemDropdown<DropdownItem<string>>
			items={items}
			selectedItem={selected}
			onChange={(next) => {
				if (next === undefined) return;
				const groupId = next === HOLIDAY_NONE ? null : next;
				if (groupId !== value) onChange(groupId);
			}}
			label={showLabel ? label : undefined}
			fieldLabel={label}
			renderValue={renderValue}
			displayEmpty
			disabled={disabled}
			fullWidth={variant === "field"}
			formControlSx={sx}
			selectSx={variant === "table" ? BORDERLESS_SELECT_SX : FIELD_SELECT_SX}
			data-testid={dataTestId}
			search={false}
		/>
	);
};
